import React, {PropTypes, Component} from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router";
import { connect } from "react-redux";
import { compose } from 'recompose';
import * as d3 from 'd3';
import { FACT_SHEET_URL, DL_SERVICE_URL } from '../../config';
import RiskFinderComponent from "../RiskFinderComponent";
import FactSheetContent from "../FactSheetContent";
import ComponentIcons from "../ComponentIcons";
import DownloadForm from "../DownloadForm";
import Modal from "../Modal";
import ToggleText from '../ToggleText';
import { SetModalVisible, SetProtection } from "../../actions";
import styles from "./Summary.css";
import withStyles from "../../decorators/withStyles";
import {
  commas, newEnglandReport, isBangladesh, isCaribbean, isIDB, isUS, sameLocationNewHash, simplePlaceName, capitalize,
  gaEvent, isEcuador, lowercase, stateAbbrev
} from "../../utils/utils";
import {SetModalTitle} from "../../actions";
import { translateValue, translateDataValue, translatePassage } from "../../translations/utils";
import { clickDownloadLink, downloadFile, getLowAndHighPathways, getModel, getModelRange, getPathway, getWaterLevelUnit, impactGroups, resolveForecastUnits, shouldTranslate, timeout, toDecimal, xlt } from "../../utils/utils";


class Summary extends RiskFinderComponent {

  static propTypes = {
    placeName: PropTypes.string.isRequired,
    waterLevel: PropTypes.object.isRequired,
    modalVisible: PropTypes.bool.isRequired,
    summary: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      printOpen: false,
      settingsOpen: false,
      impactsInfoOpen: false,
      projectionInfoOpen: false,
      dlType: "fact-sheet",
      refs: []
    };
  }

  setTxContent = el => {
    // this.state.refs.push(el);
    d3.select(el).attr("tx-content", "block");
    // d3.select(el).attr("tx-dynamic", "disabled");
  };

  emisDesc() {
    const forecastType = this.props.forecastType;
    return forecastType.includes("RCP45") ? "moderate cuts in"
      : forecastType.includes("RCP60") ? "minor cuts in"
      : forecastType.includes("RCP85") ? "unchecked"
      : "extreme cuts in";
  }

  getWhenSentences() {
    if (!this.state.data) {
      return '';
    }
    const { waterLevel, forecastType } = this.props;
    const model = getModel(forecastType);
    const bestYear = 2100; // TODO: compute bestYear
    const slr = model => this.getSLRValue(model, 2100);
    const proj = model => this.getProjValue(model, bestYear, waterLevel);
    const emisDescr = this.emisDesc();
    const lang = Transifex.live.getSelectedLanguageCode();
    const translatedEmisDescr = lang ? translateValue('emisDescr', emisDescr, lang) : emisDescr;

    let params = {
        highModel: slr('highModel'),
        lowModel: slr('lowModel'),
    };
    if (['AR5', 'BK', 'DP16'].includes(model)) {
      params = {
        ...params,
        bestYear,
        emisDesc: translatedEmisDescr,
        pathwayHigh: proj('highModel'),
        pathwayLow: proj('lowModel'),
        waterLevel: waterLevel.level,
      };
    }

    const versionKeys = {
      ACE: 'ACE',
      AR5: 'default',
      BK: 'default',
      DP16: 'default',
      NCA: 'NCA',
      NOAA2017: 'NOAA2017',
      NRC: 'NRC',
    };

    return translatePassage('whenSentences', versionKeys[model], params, lang);
  }

  getModelFootnote() {
    const { forecastType } = this.props;
    const model = getModel(forecastType);
    const pathway = getPathway(forecastType);
    const emisDesc = this.emisDesc();
    const lang = Transifex.live.getSelectedLanguageCode();
    const emisDescr = this.emisDesc();
    const translatedEmisDescr = lang ? translateValue('emisDesc', emisDesc, lang) : emisDescr;

    let params = {};
    if (['AR5', 'BK', 'DP16'].includes(model)) {
      params = {
        ...params,
        emisDesc: capitalize(translatedEmisDescr),
        pathway: toDecimal(pathway),
      };
    }

    const versionKeys = {
      ACE: 'default',
      AR5: 'AR5',
      BK: 'BK',
      DP16: 'DP16',
      NCA: 'default',
      NOAA2017: 'default',
      NRC: 'NRC',
    };

    return translatePassage('footnote', versionKeys[model], params, lang);
  }

  getProjRange(year) {
    const low = this.getProjValue("lowModel", year, this.props.waterLevel);
    const high = this.getProjValue("highModel", year, this.props.waterLevel);
    return `${low} \u2013 ${high}`;
  }

  getProjValue(forecast, year, waterLevel) {
    const proj = this.state.data.factSheet[forecast];
    const totalRisk = proj['Multi-year'].levels.find(l => l.water_level == waterLevel.level && l.water_unit == waterLevel.unit);
    const idx = totalRisk.xVals.indexOf(year);
    return `${Math.round(totalRisk.yVals[idx] * 100)}%`;
  }

  getSLRValue(forecast, year) {
    const proj = this.state.data.factSheet[forecast];
    const slr = proj['Basic'].levels.find(l => l.water_unit === this.props.waterLevel.unit);
    const idx = slr.xVals.indexOf(year);
    return Math.round(slr.yVals[idx] * 10) / 10;
  }

  getMapCaption() {
    const { mapLayer, waterLevel } = this.props;
    const unit = getWaterLevelUnit(waterLevel);
    const lang = Transifex.live.getSelectedLanguageCode();
    const params = {
      unit: translateValue('units', unit, lang),
      waterLevel: waterLevel.level,
    }
    return translatePassage('mapCaption', mapLayer, params, lang);
  }

  setASLRValues = async (callback) => {
    const { aslrStation } = this.props;
    if (aslrStation) {
      const rows = await d3.json(`/api/aslr-flooding/flood-days-decade/${aslrStation.id}`);
      const totalDays1950 = rows.map(row => row.natural_days + row.anthro_days).reduce((a, b) => a + b);
      const noanthroDays1950 = rows.map(row => row.natural_days).reduce((a, b) => a + b);
      this.setState({aslrValues: [totalDays1950, noanthroDays1950], aslrStn: aslrStation}, callback);
    } else {
      callback();
    }
  };

  getASLRFloodSentences() {
    const { placeName } = this.props;
    let params = {};
    const lang = Transifex.live.getSelectedLanguageCode();
    const version = this.state.aslrStn ? 'station' : 'default';
    if (version === 'station') {
      params = {
        ...params,
        stationLocation: this.state.aslrStn.name.split("-")[0].trim(),
        floodDays: this.state.aslrValues[0],
        baselineFloodDays: this.state.aslrValues[1],
        miles: this.state.aslrStn.dist,
        placeName: simplePlaceName(placeName),
      }
    }

    const versionKeys = {
      default: 'default',
      station: 'station',
    };

    return translatePassage('unnatural', versionKeys[version], params, lang);
  }

  openImpactsInfo = e => {
    gaEvent('modal', 'open', 'impacts-info');
    this.props.dispatch(SetModalVisible(true));
    this.setState({impactsInfoOpen: true});
  };

  closeImpactsInfo = e => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({impactsInfoOpen: false});
  };

  openProjectionInfo = e => {
    gaEvent('modal', 'open', 'projection-info');
    this.props.dispatch(SetModalVisible(true));
    this.setState({projectionInfoOpen: true});
  };

  closeProjectionInfo = e => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({projectionInfoOpen: false});
  };

  openPrint = e => {
    this.props.dispatch(SetModalVisible(true));
    this.setState({printOpen: true});
  };

  closePrint = e => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({printOpen: false});
  };

  setDlType(arg) {
    this.setState({dlType: arg});
  }

  getReport = reportType => {
    this.setDlType(reportType);
    gaEvent('modal', 'open', `download-${reportType}`);
    const title = reportType === "fact-sheet" ? "Get fact sheet" :
      reportType === "state-report" ? "Get report" :
        reportType === "fast-look" ? "Get report" : "Download";
    this.props.dispatch(SetModalTitle(title));
    this.openPrint();
  };

  toggleProtection = () => {
    if (this.props.protection === "protected") {
      this.props.dispatch(SetProtection("unprotected"));
    } else {
      this.props.dispatch(SetProtection("protected"));
    }
  };

  formatField = (datum) => {
    const lang = shouldTranslate();
    const formattedValue = commas(datum.value);
    const displayName = lang ? translateValue('factNames', datum.name, lang) : datum.name;
    const displayValue = lang ? translateDataValue(formattedValue, lang) : formattedValue;
    return `\u2022 ${displayName}: ${displayValue}\r`;
  }

  formatData = (data) => {
    let fields = [];
    data.forEach((datum) => {
      const formattedField = this.formatField(datum);
      fields.push(formattedField);
    });
    return fields;
  }

  splitSummary = (fields) => {
    if (fields.length <= 5) {
      return [fields.join(''), ''];
    } else {
      return [fields.slice(0, 3).join(''), fields.slice(3).join('')];
    }
  }

  impactSummaryStr(summary) {
    const impactSummary = Object.values(summary).find((obj) => obj.type === 'impacts');
    if (impactSummary) {
      const summary = this.formatData(impactSummary.data);
      return this.splitSummary(summary);
    } else {
      return ['', ''];
    }
  }

  conditionalFootnote(tag) {
    const map = {
      one: 1,
      um: 1,
      uno: 1,
      ten: 10,
      dez: 10,
      diez: 10,
      "1": 1,
      "2": 2,
      "3": 3,
      "4": 4,
      "three": 3,
      "quatro": 4,
      "tres": 3,
      "cuatro": 4,
      "four": 4,
      "six": 6,
      "tre": 3,
      "seis": 6,
      "two": 2,
    };

    const nums = tag.split("-");
    const tagWl = map[nums[0]];
    if (this.props.waterLevel === tagWl || (tagWl < 10 && this.props.waterLevel.level < 10)) {
      return nums.slice(1).map(num => map[num]).join(",");
    } else {
      return "";
    }
  }

  getFactSheetRequest = () => {
    const { placeType, placeID, placeName, waterLevel, mapPosition, mapLayer,
            placeContainer, stations, stationNum, summary } = this.props;
    const lang = Transifex.live.getSelectedLanguageCode();
    const ss2Unit = waterLevel.unit === "ft" ? "feet": "meters";
    const screenshotTarget = `http://ss2.climatecentral.org/widget.html#${mapPosition.zoom + 1}/${mapPosition.lat}/${mapPosition.lon}?level=${waterLevel.level}&unit=${ss2Unit}&show=${mapLayer}&lang=${lang}`;
    const dt = new Date();
    const year = dt.getUTCFullYear().toString();
    const month = (dt.getUTCMonth() + 1).toString();
    const day = dt.getUTCDate().toString();
    const [ summary1, summary2 ] = this.impactSummaryStr(summary);
    const request = {
      "request": "render",
      "template": lang === "es" ? "slr-fact-sheet-es" : "slr-fact-sheet",
      "product": "photoshop",
      "outfile": placeID,
      "tagMap": {
        "placename": xlt(simplePlaceName(placeName)),
        "caps": xlt(simplePlaceName(placeName)).toUpperCase(),
        "statelong": xlt(placeContainer.name),
        "stateshort": placeContainer.abbrev,
        "I": waterLevel.level,
        "ten-one-2": this.conditionalFootnote("ten-one-2"),
        "one-1-two": this.conditionalFootnote("one-1-two"),
        "um-three-quatro": this.conditionalFootnote("um-three-quatro"),
        "dez-tres-4": this.conditionalFootnote("dez-tres-4"),
        "uno-3-cuatro-seis": this.conditionalFootnote("uno-3-cuatro-seis"),
        "diez-tre-four-six": this.conditionalFootnote("diez-tre-four-six"),
        summary1,
        summary2,
        "hslr": Math.round(this.state.data.factSheet.fastLook.PeriodSLR * 12),
        // "slr30": Math.round(this.getSLRValue("rcp45", 2030) * 12),
        // "multiplier": this.getMultiplier("rcp45", 2030, waterLevel),
        "hof": Math.round(this.state.data.factSheet.fastLook.HIGHOBS * 10) / 10,
        "hoyr": parseInt(this.state.data.factSheet.fastLook.HIGHYR).toString(),
        "flood100": Math.round(this.state.data.factSheet.fastLook.RL100 * 10) / 10,
        // TODO: make this a thing (ask Dan R. to ask Scott/Ben)
        "yr1": xlt('not in record'),
        "wl2": this.state.data.factSheet.fastLook.HILEV,
        "tide-gauge": stations[stationNum].name,
        "miles": stations[stationNum].dist,
        "unnatural": xlt(this.getASLRFloodSentences()),
        "fn6": xlt(this.getModelFootnote()),
        "30range": this.getProjRange(2030),
        "50range": this.getProjRange(2050),
        "100range": this.getProjRange(2100),
        "tgyr1": parseInt(this.state.data.factSheet.fastLook.ANALYSISSTART).toString(),
        "tgyr2": parseInt(this.state.data.factSheet.fastLook.ANALYSISEND).toString(),
        "lyr": parseInt(this.state.data.factSheet.fastLook.ANALYSISEND - this.state.data.factSheet.fastLook.ANALYSISSTART).toString(),
        "url-name1": this.state.data.factSheet.resources[0].source,
        "url-name2": this.state.data.factSheet.resources[1].source,
        "url-name3": this.state.data.factSheet.resources[2].source,
        "url1": this.state.data.factSheet.resources[0].url,
        "url2": this.state.data.factSheet.resources[1].url,
        "url3": this.state.data.factSheet.resources[2].url,
        "when-sentences": xlt(this.getWhenSentences()),
        day,
        "mo": month,
        "currentyear": year,
        "MAP_IMAGE": "",
        "mapcaption": xlt(this.getMapCaption())
      },
      "resources": {
        "MAP_IMAGE": DL_SERVICE_URL + "/screenshot" + "?width=1366&height=700&url=" + encodeURIComponent(screenshotTarget)
      }
    };
    return request;
  };

  createFactSheet = (callback) => {
    const { placeID } = this.props;
    const data = this.getFactSheetRequest();
    const url = `${FACT_SHEET_URL}/create-template`;

    timeout(300000,
      d3.json(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      .then((response) => {
        const url = response.url;
        const filename = `${placeID}.pdf`;
        console.log('CFS WTF', FACT_SHEET_URL, url, filename);
        clickDownloadLink(url, filename);
        window.URL.revokeObjectURL(url);
        if (callback) {
          callback(null, url, filename);
        }
      })
      .catch((error) => callback(error))
    );
  };

  getPathwayData = async () => {
    const { forecastType, placeType, placeID, stationNum } = this.props;
    const { pathwayLow, pathwayHigh } = getLowAndHighPathways(forecastType);
    const urls = [
      `/api/forecast/${placeType}/${placeID}/${pathwayLow}?stationNum=${stationNum}&slrdb=true`,
      `/api/forecast/${placeType}/${placeID}/${pathwayHigh}?stationNum=${stationNum}&slrdb=true`,
    ];
    const results = await Promise.all(urls.map((url) => d3.json(url)));
    const [ pathwayLowData, pathwayHighData ] = results;
    const pathwayLowFormatted = resolveForecastUnits(pathwayLowData);
    const pathwayHighFormatted = resolveForecastUnits(pathwayHighData);
    return { pathwayLow: pathwayLowFormatted, pathwayHigh: pathwayHighFormatted };
  }

  setPathwayData = async (callback) => {
    const pathwayData = await this.getPathwayData();
    this.setState({
      data: {
        factSheet: Object.assign({}, this.state.data.factSheet, pathwayData)}
      }, callback);
  };

  getModelData = async () => {
    const { forecastType, placeType, placeID, stationNum } = this.props;
    const [ lowModel, highModel ] = getModelRange(forecastType);
    const urls = [
      `/api/forecast/${placeType}/${placeID}/${lowModel}?stationNum=${stationNum}&slrdb=true`,
      `/api/forecast/${placeType}/${placeID}/${highModel}?stationNum=${stationNum}&slrdb=true`,
      `/api/forecast/${placeType}/${placeID}/fast-look?stationNum=${stationNum}&columns=HIGHOBS,HIGHYR,RL100,ANALYSISSTART,ANALYSISEND,HILEV,PeriodSLR,RL1,RL10,RL100`,
      `/api/resources/${placeType}/${placeID}`,
    ];
    const results = await Promise.all(urls.map((url) => d3.json(url)));
    const [ lowModelRaw, highModelRaw, fastLook, resources ] = results;
    const lowModelFormatted = resolveForecastUnits(lowModelRaw);
    const highModelFormatted = resolveForecastUnits(highModelRaw);
    return { lowModel: lowModelFormatted, highModel: highModelFormatted, fastLook, resources };
  }

  setModelData = async (callback) => {
    const { aslrStation } = this.props;
    const modelData = await this.getModelData();
    this.setState({
      aslrStn: aslrStation,
      aslrValues: ["not available", "not available"],
      data: {
        factSheet: modelData,
      }
    }, callback);
  };

  loadFactSheetData = async (callback) => {
    const { forecastType } = this.props;
    const self = this;
    if (forecastType.includes('BK') || forecastType.includes('AR5') || forecastType.includes('DP16')) {
      this.setModelData(() => self.setPathwayData(() => self.setASLRValues(callback)));
    } else {
      this.setModelData(() => self.setASLRValues(callback));
    }
  }

  downloadReport = (dlType, callback) => {
    const { placeType, placeID } = this.props;
    const lang = Transifex.live.getSelectedLanguageCode();
    const url = `/api/reports/${placeType}/${placeID}/${dlType}?lang=${lang}`;
    downloadFile(url, null, callback);
  };

  dlCallback = (onDlComplete) => {
    const dlType = this.state.dlType;
    if (dlType === "fact-sheet") {
      const callback = () => this.createFactSheet(onDlComplete);
      this.loadFactSheetData(callback);
    } else {
      // Add artificial delay so that people will look at our pre-download advertisements.
      setTimeout(() => {
        this.downloadReport(dlType, onDlComplete);
      }, 10);
    }
  };

  componentDidMount() {
    // if (isUS(this.props.placeID)) {
    //   this.loadFactSheetData();
    // }
  }

  render() {
    const { placeName, placeType, placeID, waterLevel, protection, placeContainer, forecastName, forecastType } = this.props;
    const showStateReport = isUS(placeID) && (placeType === "state" ? !newEnglandReport(stateAbbrev(placeType, placeID)) :
                          !newEnglandReport(placeContainer.abbrev));
    const stateOutsideNE = placeType == "state" && !newEnglandReport(stateAbbrev(placeType, placeID));
    const showFactSheet = isUS(placeID) && forecastType !== "noGW" && !stateOutsideNE;

    var summaries = [];
    var keys = Object.keys(this.props.summary);
    keys.sort();
    keys.forEach(k => {
      summaries.push(this.props.summary[k]);
    });
    const FCAST_SUMMARY = 0;
    const IMPACT_SUMMARY = 1;
    var populationInSummary;
    if (this.props.summary[IMPACT_SUMMARY]) {
      var pop = impactGroups.Population.find(exposure_id => {
        var data = this.props.summary[IMPACT_SUMMARY].data;
        populationInSummary = data.find(obj => obj.name === exposure_id);
        return populationInSummary !== undefined;
      });
      if (!pop) {
        populationInSummary = null;
      }
    } else {
      populationInSummary = null;
    }

    // const populationInSummary = this.props.summary[IMPACT_SUMMARY] ?
    //   this.props.summary[IMPACT_SUMMARY].data.find(obj => obj.name === "Population") : null;
    const peopleAtRisk = populationInSummary ? populationInSummary.value : "";

    const reportDesc = this.state.dlType === "fact-sheet" ?
      "The fact sheet is a free customizable 2-page PDF handout of sea level and coastal flood risk " +
      "information specific to the area and settings you choose." :
      this.state.dlType === "fast-look" ?
        "The brief report is a free 4-page PDF report on sea level and coastal flood risk for the area you choose, " +
        "including a short description of methods and assumptions, as well as key takeaways." :
        this.state.dlType === "state-report" ?
          "The state report is a free detailed report for the state you choose, including sea level and coastal flood " +
          "exposure analysis, risk projections, and technical methodology." :
          "";

    const fcastSummaryText = this.props.summary[FCAST_SUMMARY] ?
      lowercase(this.props.summary[FCAST_SUMMARY].data.summaryText)
        .replace("localizing", "localized")
        .replace("but that is assuming", "assuming")
      : "";

    var riskPct;
    if (this.props.summary[FCAST_SUMMARY]) {
      if (this.props.summary[FCAST_SUMMARY].data.riskPct2 < 0.001) {
        riskPct = "<0.1";
      } else if (this.props.summary[FCAST_SUMMARY].data.riskPct2 < 0.01) {
        riskPct = "<1";
      } else {
        riskPct = Math.round(this.props.summary[FCAST_SUMMARY].data.riskPct2 * 100);
      }
    } else {
      riskPct = "";
    }

    const summaryList = [
      <li key={"1"}>Warming oceans and melting glaciers and ice sheets are raising global sea levels.</li>,
      peopleAtRisk ?
          <li ref={this.setTxContent} key={Math.random()}>About <var>{commas(peopleAtRisk)}</var> people in <var>{xlt(simplePlaceName(placeName))}</var> live on {(protection === "protected" ? "exposed" : "")} land
                  below <var>{waterLevel.level}</var> <var>{waterLevel.unit}</var> (the selected
                  level) <span className="icon info" onClick={this.openImpactsInfo}/>. <Link to={sameLocationNewHash("#impacts-in-depth")}>More
                  threats</Link><Link className="scroll-arrow" to={sameLocationNewHash("#impacts-in-depth")}>&#8595;</Link>
          </li>
          : null,
      this.props.stations.length > 0 ?
          <li ref={this.setTxContent} key={Math.random()}>
            The selected sea level scenario <span className="icon info" onClick={this.openProjectionInfo} /> points to
                a <var>{riskPct}</var>% risk of
                at least one flood over <var>{waterLevel.level}</var> <var>{waterLevel.unit}</var> taking place between today
                  and 2050 in
                the <var>{xlt(simplePlaceName(placeName))}</var> area. <Link to={sameLocationNewHash("#threat-forecast")}>More scenarios</Link><Link className="scroll-arrow" to={sameLocationNewHash("#threat-forecast")}>&#8595;</Link>
          </li>
          : null,
      isUS(placeID) ?
        <li key={"4"}>
          Learn about <Link to={sameLocationNewHash("#explore-further")}>related places</Link><Link className="scroll-arrow" to={sameLocationNewHash("#explore-further")}>&#8595;</Link> and <Link to={sameLocationNewHash("#reducing-your-risk")}>how
                to reduce risks</Link><Link className="scroll-arrow" to={sameLocationNewHash("#reducing-your-risk")}>&#8595;</Link>
        </li>
        : null
    ];

    const downloadSection = (
      <div className="download-section">
        <div className="left-col">
          <h5>DOWNLOADS</h5>
          <div className="button-row">
            {showFactSheet ? <div className="report-link" onClick={this.getReport.bind(null, "fact-sheet")}>Local fact sheet<br/><span className="dl-description">Essential info, linked to current settings (2pp.)</span></div> : null}
            {showFactSheet ? <div className="report-link" onClick={this.getReport.bind(null, "fast-look")}>Local report<br/><span className="dl-description">More details (4pp.)</span></div> : null}
            {showStateReport ? <div className="report-link" onClick={this.getReport.bind(null, "state-report")}>State report<br/><span className="dl-description">State findings and technical methods</span></div> : null}
            {isBangladesh(placeID) ? <div className="report-link" onClick={this.getReport.bind(null, "bangladesh")}>Report for Bangladesh<br/><span className="dl-description">Detailed analysis</span></div> : null}
            {isCaribbean(placeID) ? <div className="report-link" onClick={this.getReport.bind(null, "caribbean")}>Report for the Caribbean<br/><span className="dl-description">Detailed analysis</span></div> : null}
          </div>
        </div>
        <div className="right-col">
          { isBangladesh(placeID) ?
              <p ref={this.setTxContent} key={Math.random()}>This PDF presents analysis results and methodology for all of Bangladesh
              including <var>{xlt(simplePlaceName(placeName))}</var>. Find customizable slide, map and data downloads below.
              </p> :
            ((showStateReport && !showFactSheet) || !isUS(placeID)) ?
            <p ref={this.setTxContent} key={Math.random()}>This PDF download summarizes key information from this tool
              for <var>{xlt(simplePlaceName(placeName))}</var>. Find customizable slide, map and data downloads below.
            </p> :
            placeType === "state" && newEnglandReport(stateAbbrev) ?
              <p ref={this.setTxContent} key={Math.random()}>These PDF downloads summarize key information from this tool
                for <var>{xlt(simplePlaceName(placeName))}</var>. Find customizable slide, map and data downloads below.
              </p> :
              placeType !== "state" && !showStateReport ?
                <p ref={this.setTxContent} key={Math.random()}>These PDF downloads summarize key information from this tool
                  for <var>{xlt(simplePlaceName(placeName))}</var>. Find customizable slide, map and data downloads below.
                </p>
                :
            <p ref={this.setTxContent} key={Math.random()}>These PDF downloads summarize key information from this tool,
              for <var>{xlt(simplePlaceName(placeName))}</var>{placeType !== "state" ? <span> ("local" items) or for <var>{xlt(placeContainer.name)}</var></span> : ""}. Find
              customizable slide, map and data downloads below.
            </p>
          }
          <p>Have more specific needs? <a href="http://sealevel.climatecentral.org/maps/custom-analysis" target="_blank"
                                          onClick={() => gaEvent('custom-analysis', 'click', 'summary')} >Learn about our custom work</a></p>
        </div>
      </div>
    );

    const idbLogo = (
      <div className="logo-container">
        <img className="cc-logo" src="/static/cc_logo_stacked_flat.png" />
        <img className="idb-logo" src="/static/idb_transparent_back.png" />
      </div>
    );

    return (
      <div className="Summary">
        <div className="header">
          <a className="anchor" id="summary"/>
          <Link className="hash-link" to={sameLocationNewHash("#summary")}>
            <h1>Summary <span className="anchor-hash"><emph>#</emph></span></h1>
          </Link>
        </div>
        <div className="summary-text">
          {this.props.children}
          <div className="toplinks small-text">Scroll or change settings for more info | <a href={isIDB(placeID) ? "https://www.youtube.com/watch?v=l5mw2FSaf_s" : "https://www.youtube.com/watch?v=Al023-Y7OIo"} target="_blank">Tutorial video</a></div>
          {summaries.length === 0 ? <p></p> : null}
          <div className="text-column">
            <ul className="spaced-list">
              {summaryList}
            </ul>
            {!isEcuador(placeID) ? downloadSection : idbLogo}
          </div>
        </div>
        <div className="clear"></div>

        <Modal isOpen={this.state.printOpen && this.props.modalVisible}
               handleClick={this.closePrint}
        >
          <section>
            <DownloadForm
              id="ReportDLForm"
              placeType={placeType}
              placeID={placeID}
              dlType={this.state.dlType}
              productID="reports"
              dlCallback={this.dlCallback.bind(this)}
            >
              <p>{reportDesc} </p>
            </DownloadForm>
          </section>
        </Modal>

        <Modal
          isOpen={this.state.impactsInfoOpen && this.props.modalVisible}
          handleClick={this.closeImpactsInfo}
          title="About sea level rise exposure"
        >
          {protection === "protected" ?
            `Values exclude sub-<var>${waterLevel.level}</var><var>${waterLevel.unit}</var> areas potentially protected by levees or other features.
             To include these areas, scroll down to the section "What Is at Risk?" and, below the table, click on the
              underlined word "exclude" to toggle it to "include."` :
            `Values include sub-<var>${waterLevel.level}</var><var>${waterLevel.unit}</var> areas potentially protected by levees or other features.
             To exclude these areas, scroll down to the section "What Is at Risk?" and, below the table, click on the
              underlined word "include" to toggle it to "exclude."`}
        </Modal>

        <Modal
          isOpen={this.state.projectionInfoOpen && this.props.modalVisible}
          handleClick={this.closeProjectionInfo}
          title="About sea level rise projections"
        >
          Projections are {fcastSummaryText}. To change, scroll down to the section "When Are the Risks?" and adjust
          settings using the sea level scenario buttons to the right of the figure. Use the gear icon for advanced
          settings.
        </Modal>

        {isUS(placeID) ?
            <FactSheetContent
                summary={this.props.summary ? this.props.summary : ""}
                whenSentences={this.getWhenSentences()}
                modelfootnote={this.getModelFootnote()}
                unnatural={this.getASLRFloodSentences()}
                yr1="not in record"
                statelong={placeContainer.name}
                placename={simplePlaceName(placeName)}
            >
              {this.props.mapLayer === "sovi" ?
                  <p>Social vulnerability (e.g. from low income) compounds coastal risk. Land below
                    <var>{waterLevel.level}</var> <var>{waterLevel.unit}</var> is
              colored according to the legend. Surging Seas uses high-accuracy lidar elevation data supplied by NOAA.
              Map reflects a uniform sea level and/or flood height. Individual storm surge, tidal or rainfall events
              cause more complex and uneven water surfaces.
                  </p>
                  : this.props.mapLayer === "property" ?
                  <p>Land below <var>{waterLevel.level}</var> <var>{waterLevel.unit}</var> is colored according to the legend. Surging Seas uses
                   high-accuracy lidar elevation data supplied by NOAA. Map reflects a uniform sea level and/or flood height.
                   Individual storm surge, tidal or rainfall events cause more complex and uneven water surfaces.</p>
                  : <p>Land below <var>{waterLevel.level}</var> <var>{waterLevel.unit}</var></p>
              }
            </FactSheetContent>
        : null}
      </div>
    );

  };
}

export default compose(
  withStyles(styles),
  connect((state) => state),
)(Summary);
